import React from 'react';
import { Building2, Check, Edit2, Trash2 } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { AdminPlanResponse } from '../hooks/useAdminPlans';

interface PlanCardProps {
  plan: AdminPlanResponse;
  onEdit: () => void;
  onDelete: () => void;
}

export const PlanCard: React.FC<PlanCardProps> = ({ plan, onEdit, onDelete }) => {
  let featArr: string[] = [];
  if (Array.isArray(plan.features)) {
    featArr = plan.features;
  } else if (typeof plan.features === 'string') {
    try {
      featArr = JSON.parse(plan.features);
    } catch {
      featArr = [];
    }
  }

  return (
    <Card
      className={`p-5 bg-cf-surface flex flex-col justify-between transition-all ${
        plan.is_default
          ? 'border-cf-orange/40 shadow-xs'
          : 'border-cf-border hover:border-cf-borderActive'
      }`}
    >
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-cf-textPrimary">{plan.name}</h2>
              {plan.is_default && (
                <Badge variant="orange" size="xs">
                  DEFAULT
                </Badge>
              )}
            </div>
            <span className="text-xs text-cf-textMuted font-sans">{plan.slug}</span>
          </div>

          <Badge variant={plan.is_active ? 'success' : 'default'} size="xs">
            {plan.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        <p className="text-xs text-cf-textSecondary mb-4 min-h-[32px] line-clamp-2">
          {plan.description || 'No description provided.'}
        </p>

        <div className="flex items-baseline gap-1.5 mb-4">
          <span className="text-2xl font-extrabold text-cf-textPrimary tabular-nums">
            ${((plan.price_monthly_cents || 0) / 100).toFixed(0)}
          </span>
          <span className="text-xs text-cf-textMuted">/ month</span>
          {(plan.price_yearly_cents || 0) > 0 && (
            <span className="text-xs text-cf-textMuted ml-1">
              (${((plan.price_yearly_cents || 0) / 100).toFixed(0)} / yr)
            </span>
          )}
        </div>

        {/* Quotas Box */}
        <div className="grid grid-cols-2 gap-2.5 p-3 rounded bg-cf-bg border border-cf-border text-xs mb-4">
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Submissions</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums">
              {(plan.max_monthly_submissions || 0).toLocaleString()} / mo
            </span>
          </div>
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Total Storage</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums">
              {plan.max_storage_bytes && plan.max_storage_bytes >= 1073741824
                ? `${(plan.max_storage_bytes / 1073741824).toFixed(0)} GB`
                : `${Math.round((plan.max_storage_bytes || 104857600) / 1048576)} MB`}
            </span>
          </div>
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Max File Size</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums text-cf-orange">
              {Math.round((plan.max_file_size_bytes || 10485760) / 1048576)} MB / file
            </span>
          </div>
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Datastores</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums">
              {plan.max_tables} tables
            </span>
          </div>
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Seats</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums">
              {plan.max_team_seats} seats
            </span>
          </div>
          <div>
            <span className="text-xs text-cf-textMuted uppercase block">Retention</span>
            <span className="font-semibold text-cf-textPrimary tabular-nums">
              {plan.retention_days} days
            </span>
          </div>
        </div>

        {/* Features Bullet List */}
        {featArr.length > 0 && (
          <ul className="space-y-1.5 text-xs text-cf-textSecondary mb-4">
            {featArr.map((f, idx) => (
              <li key={idx} className="flex items-start gap-1.5">
                <Check size={13} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer Bar */}
      <div className="pt-3 border-t border-cf-border flex items-center justify-between text-xs">
        <span className="text-cf-textMuted flex items-center gap-1">
          <Building2 size={13} />
          <span>{plan.organizations_count ?? 0} active orgs</span>
        </span>

        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onEdit}
            className="border border-cf-border"
            title="Edit Plan"
          >
            <Edit2 size={13} />
          </Button>
          {!plan.is_default && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={onDelete}
              className="hover:bg-rose-500/10 border border-cf-border text-cf-textMuted hover:text-rose-400"
              title="Delete Plan"
            >
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
